import {
  EntityHeader,
  EntityName,
  EntityTabs,
  type EntityTabItem,
  useActiveEntityTab,
} from "@/shared/components/EntityPage";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { hasSessionEvents } from "@/shared/domain/orgs";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { type SessionRecord } from "@/shared/domain/sessions";
import { EntityIcon, FileText, Info } from "@/shared/foundation/icons";
import { SessionTab } from "@/shared/navigation";

import { SessionActionStatus } from "./SessionActionStatus";
import { SessionHeader } from "./SessionHeader";

export interface SessionChromeProps {
  /** URL-derived session id. Used as the heading when ``session`` is ``null``. */
  sessionId: string;
  /**
   * Resolved session record, or ``null`` when the parent's ``useSession``
   * fetch is pending or failed. When ``null`` the heading falls back to
   * ``sessionId`` and the rest of the header is omitted; the tab strip
   * renders unconditionally because it reads its active value from the URL.
   */
  session: SessionRecord | null;
}

/**
 * Header and tab bar for ``/sessions/:sessionId``. Rendered on both the
 * success and error branches so the user sees the same heading hierarchy
 * whether or not the session record resolved.
 */
export function SessionChrome({ sessionId, session }: SessionChromeProps) {
  // The same rule gates the /sessions/:sessionId/events route in SessionsRoutes.
  const eventsTabEnabled = hasSessionEvents(useOrgEventModel(session?.org_id));
  // The flag also gates these tabs' routes (SessionsRoutes.tsx), in the same
  // org, so the strip lists only tabs that can be reached.
  const aiTabsEnabled = useCheckFeatureFlag(
    "release-session-ai",
    session?.org_id,
  );
  const tabs: EntityTabItem<SessionTab>[] = [
    {
      value: SessionTab.Topics,
      label: "Topics",
      icon: <EntityIcon type="topic" />,
    },
    {
      value: SessionTab.Sources,
      label: "Sources",
      icon: <EntityIcon type="file" />,
    },
    ...(eventsTabEnabled
      ? [
          {
            value: SessionTab.Events,
            label: "Events",
            icon: <EntityIcon type="event" />,
          },
        ]
      : []),
    {
      value: SessionTab.Metrics,
      label: "Metrics",
      icon: <EntityIcon type="metric" />,
    },
    "divider",
    {
      value: SessionTab.Actions,
      label: "Actions",
      icon: <EntityIcon type="action" />,
      endAdornment: <SessionActionStatus sessionId={sessionId} />,
    },
    ...(aiTabsEnabled
      ? ([
          {
            value: SessionTab.Agents,
            label: "Agents",
            icon: <EntityIcon type="agent" />,
          },
          "divider",
          { value: SessionTab.Summary, label: "Summary", icon: <FileText /> },
        ] satisfies EntityTabItem<SessionTab>[])
      : (["divider"] satisfies EntityTabItem<SessionTab>[])),
    { value: SessionTab.Details, label: "Details", icon: <Info /> },
  ];
  // The tab reads from the URL alone, so the strip renders on the error
  // branch too, before or without the session record.
  const activeTab = useActiveEntityTab(tabs);

  return (
    <div>
      {session ? (
        <SessionHeader session={session} />
      ) : (
        <EntityHeader
          type="session"
          name={<EntityName>{sessionId}</EntityName>}
        />
      )}
      <EntityTabs tabs={tabs} activeTab={activeTab} aria-label="session tabs" />
    </div>
  );
}
